/**
 * FilterFieldsView - Shared filter fields for similar players filtering
 * @module features/similar-players/components/FilterFields
 */

import React, { memo } from "react";

import {
  MAX_AGE,
  MAX_COST,
  MAX_MONTHS_REMAINING,
  MIN_AGE,
  MIN_COST,
  MIN_MONTHS_REMAINING,
} from "@/features/similar-players/stores/filterValues";

import CustomMultiSelectContainer from "@/components/organisms/MultiSelect";
import MultiSlider from "@/components/organisms/MultiSlider";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import {
  RosterManagementLeagueFragment,
  RosterManagementNationalityFragment,
} from "@/generated/graphql";

/**
 * Props for FilterFieldsView component
 */
interface Props {
  groupedLeagues: RosterManagementLeagueFragment[];
  loadingLeagues: boolean;
  leaguesValue: RosterManagementLeagueFragment[];
  nationalities: RosterManagementNationalityFragment[];
  loadingNationalities: boolean;
  nationalitiesValue: RosterManagementNationalityFragment[];
  ageValue: number[];
  salaryValue: number[];
  monthsRemainingValue: number[];
  isSmallScreen?: boolean;
  onSelectLeagues?: (value: RosterManagementLeagueFragment[]) => void;
  onSelectNationalities?: (
    value: RosterManagementNationalityFragment[]
  ) => void;
  onChangeAge?: (value: number[]) => void;
  onChangeSalary?: (value: number[]) => void;
  onChangeMonthsRemaining?: (value: number[]) => void;
}

/**
 * Shared filter fields component for leagues, age, cost, and months remaining
 * @param root0 - Component props
 * @param root0.groupedLeagues - Array of available leagues to filter by
 * @param root0.loadingLeagues - Loading state for leagues data
 * @param root0.leaguesValue - Currently selected leagues
 * @param root0.nationalities - Array of available nationalities to filter by
 * @param root0.loadingNationalities - Loading state for nationalities data
 * @param root0.nationalitiesValue - Currently selected nationalities
 * @param root0.ageValue - Current age range filter values [min, max]
 * @param root0.salaryValue - Current salary range filter values [min, max]
 * @param root0.monthsRemainingValue - Current months remaining range filter values [min, max]
 * @param root0.isSmallScreen - Whether the screen is mobile sized (affects spacing)
 * @param root0.onSelectLeagues - Callback when leagues selection changes
 * @param root0.onSelectNationalities - Callback when nationalities selection changes
 * @param root0.onChangeAge - Callback when age range changes
 * @param root0.onChangeSalary - Callback when salary range changes
 * @param root0.onChangeMonthsRemaining - Callback when months remaining range changes
 */
const FilterFieldsView = ({
  groupedLeagues,
  loadingLeagues,
  leaguesValue,
  nationalities,
  loadingNationalities,
  nationalitiesValue,
  ageValue,
  salaryValue,
  monthsRemainingValue,
  isSmallScreen = true,
  onSelectLeagues,
  onSelectNationalities,
  onChangeAge,
  onChangeSalary,
  onChangeMonthsRemaining,
}: Props) => (
  <>
    <CustomMultiSelectContainer<RosterManagementLeagueFragment>
      data={groupedLeagues}
      listSelected={leaguesValue}
      title="Leagues"
      loading={loadingLeagues}
      onSelect={onSelectLeagues}
    />
    <CustomMultiSelectContainer<RosterManagementNationalityFragment>
      data={nationalities}
      listSelected={nationalitiesValue}
      title="Nationalities"
      loading={loadingNationalities}
      onSelect={onSelectNationalities}
    />
    <Stack key="1" className={!isSmallScreen ? "gap-3 pt-8" : "gap-6"}>
      <Heading className="font-body text-body uppercase" variant="title-sm">
        Age
      </Heading>
      <MultiSlider
        value={ageValue}
        minValue={MIN_AGE}
        maxValue={MAX_AGE}
        onChange={onChangeAge}
      />
    </Stack>
    <Stack key="2" className={!isSmallScreen ? "gap-3 pt-8" : "gap-6 pt-4"}>
      <Heading className="font-body text-body uppercase" variant="title-sm">
        Cost
      </Heading>
      <MultiSlider
        value={salaryValue}
        step={100000}
        minValue={MIN_COST}
        maxValue={MAX_COST}
        isCurrency
        onChange={onChangeSalary}
        useLogScale
      />
    </Stack>
    <Stack
      key="3"
      className={!isSmallScreen ? "gap-3 py-8" : "gap-6 pb-12 pt-4"}
    >
      <Heading className="font-body text-body uppercase" variant="title-sm">
        Months Remaining
      </Heading>
      <MultiSlider
        value={monthsRemainingValue}
        step={1}
        minValue={MIN_MONTHS_REMAINING}
        maxValue={MAX_MONTHS_REMAINING}
        onChange={onChangeMonthsRemaining}
      />
    </Stack>
  </>
);

FilterFieldsView.displayName = "FilterFieldsView";

export default memo(FilterFieldsView);
